import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  AdmissionApplication,
  AdmissionConvertInput,
  AdmissionConvertResult,
  AdmissionDetail,
  AdmissionInput,
  AdmissionSummary,
  CalendarEvent,
  CalendarEventInput,
  Certificate,
  CertificateInput,
  CertificateVerification,
  FeeReminderResult,
  LeaveOptions,
  LeaveRequest,
  LeaveRequestInput,
  MessagingPreferences,
  MessagingSettings,
  MessagingSettingsUpdate,
  OutboundMessage,
  OutboxSummary,
  AcademicRecord,
  AcademicYear,
  AcademicYearInput,
  Announcement,
  AnnouncementInput,
  AnnouncementUpdate,
  AppNotification,
  StudentExam,
  AudienceOption,
  ChildFees,
  ProvisionInput,
  ProvisionResult,
  SectionRoster,
  UserAccount,
  FeeInvoice,
  FeePayment,
  FeeStructure,
  FeeStructureInput,
  FeeStructureUpdate,
  FeeSummary,
  InvoiceDesign,
  InvoiceRun,
  InvoiceRunResult,
  PaymentInput,
  ParentDashboard,
  SchoolAdminDashboard,
  StudentDashboard,
  SuperAdminDashboard,
  TeacherDashboard,
  AttendanceDay,
  AttendanceGrid,
  AttendanceRegister,
  AttendanceSession,
  AttendanceSubmission,
  AttendanceTrend,
  ClassTeacherLink,
  CoverageRow,
  Exam,
  ExamInput,
  ExamPaper,
  ExamPaperInput,
  ExamPaperUpdate,
  ExamResult,
  ExamUpdate,
  ReportCardDesign,
  Homework,
  HomeworkInput,
  HomeworkUpdate,
  ChildLinkInput,
  ImportPreview,
  ImportResult,
  ParentImportResult,
  PrivilegeMatrix,
  TeacherImportResult,
  StructureImportPreview,
  StructureImportResult,
  ChildLinkUpdate,
  GuardianLink,
  GuardianRequest,
  MyProfile,
  MyProfileUpdate,
  GuardianLinkInput,
  StaffTask,
  StaffTaskCounts,
  StaffTaskInput,
  StaffTaskUpdate,
  TaskAssignee,
  UserAccountUpdate,
  StudentHomework,
  Submission,
  SubmissionBoard,
  MarksSheet,
  MarksSubmission,
  PromotionCandidate,
  PromotionDecision,
  PromotionOutcome,
  AcademicYearUpdate,
  Administrator,
  AdministratorUpdate,
  AuditEntry,
  AuditFilters,
  Branch,
  BranchInput,
  BranchUpdate,
  ClassCreate,
  ClassFormat,
  ClassFormatInput,
  ClassSubject,
  ClassSubjectAdd,
  ClassUpdate,
  Enrollment,
  EnrollmentInput,
  EnrollmentUpdate,
  Page,
  Parent,
  ParentInput,
  ParentUpdate,
  School,
  Plan,
  PlanCatalogue,
  PlanInput,
  SchoolPlanUsage,
  SchoolClass,
  SchoolInput,
  SchoolUpdate,
  Section,
  SectionInput,
  SectionUpdate,
  Student,
  StudentAttendance,
  StudentDetail,
  StudentInput,
  StudentUpdate,
  Subject,
  SubjectInput,
  SubjectUpdate,
  Teacher,
  TeacherAssignment,
  TeacherAssignmentInput,
  TeacherAssignmentUpdate,
  TeacherInput,
  TeacherUpdate,
  TimetableSlot,
  TimetableSlotInput,
  TimetableSlotUpdate
} from '../models';

/** Turns a plain object into HttpParams, dropping empty values. */
function toParams(source: Record<string, string | number | boolean | null | undefined>): HttpParams {
  let params = new HttpParams();
  for (const [key, value] of Object.entries(source)) {
    if (value !== null && value !== undefined && value !== '') {
      params = params.set(key, String(value));
    }
  }
  return params;
}

/**
 * Every endpoint the app talks to.
 *
 * Updates are PATCH with only the changed keys, which is what makes "leave the
 * phone number alone" different from "clear the phone number" — components
 * build those payloads with `changedFields()` in core/util/diff.ts rather than
 * posting the whole form back.
 */
@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiUrl;

  // ---------------------------------------------------------------- Schools
  listSchools(query: { q?: string; page?: number; page_size?: number } = {}): Observable<Page<School>> {
    return this.http.get<Page<School>>(`${this.base}/schools`, { params: toParams(query) });
  }

  getSchool(id: number): Observable<School> {
    return this.http.get<School>(`${this.base}/schools/${id}`);
  }

  createSchool(payload: SchoolInput): Observable<School> {
    return this.http.post<School>(`${this.base}/schools`, payload);
  }

  updateSchool(id: number, payload: SchoolUpdate): Observable<School> {
    return this.http.patch<School>(`${this.base}/schools/${id}`, payload);
  }

  uploadSchoolLogo(id: number, file: File): Observable<School> {
    const body = new FormData();
    body.append('file', file);
    return this.http.post<School>(`${this.base}/schools/${id}/logo`, body);
  }

  /** Suspend or reinstate a school. The reason is required and is audited. */
  // ------------------------------------------------------------- Plans
  planCatalogue(): Observable<PlanCatalogue> {
    return this.http.get<PlanCatalogue>(`${this.base}/plans/catalogue`);
  }

  listPlans(): Observable<Plan[]> {
    return this.http.get<Plan[]>(`${this.base}/plans`);
  }

  getPlan(id: number): Observable<Plan> {
    return this.http.get<Plan>(`${this.base}/plans/${id}`);
  }

  createPlan(payload: PlanInput): Observable<Plan> {
    return this.http.post<Plan>(`${this.base}/plans`, payload);
  }

  updatePlan(id: number, payload: Partial<PlanInput>): Observable<Plan> {
    return this.http.patch<Plan>(`${this.base}/plans/${id}`, payload);
  }

  deletePlan(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/plans/${id}`);
  }

  /** A school's plan and how much of it is used. Readable by its own office. */
  getSchoolPlan(schoolId: number): Observable<SchoolPlanUsage> {
    return this.http.get<SchoolPlanUsage>(`${this.base}/schools/${schoolId}/plan`);
  }

  /** Put a school on a plan, or `null` to take it off any plan. */
  assignSchoolPlan(schoolId: number, planId: number | null): Observable<SchoolPlanUsage> {
    return this.http.put<SchoolPlanUsage>(`${this.base}/schools/${schoolId}/plan`, { plan_id: planId });
  }

  setSchoolStatus(id: number, status: string, reason: string): Observable<School> {
    return this.http.post<School>(`${this.base}/schools/${id}/status`, null, {
      params: toParams({ status, reason })
    });
  }

  // --------------------------------------------------------------- Branches
  listBranches(schoolId: number): Observable<Branch[]> {
    return this.http.get<Branch[]>(`${this.base}/schools/${schoolId}/branches`);
  }

  createBranch(schoolId: number, payload: BranchInput): Observable<Branch> {
    return this.http.post<Branch>(`${this.base}/schools/${schoolId}/branches`, payload);
  }

  updateBranch(schoolId: number, branchId: number, payload: BranchUpdate): Observable<Branch> {
    return this.http.patch<Branch>(`${this.base}/schools/${schoolId}/branches/${branchId}`, payload);
  }

  /** Who can administer this school, read from live role grants. */
  listAdministrators(schoolId: number): Observable<Administrator[]> {
    return this.http.get<Administrator[]>(`${this.base}/schools/${schoolId}/administrators`);
  }

  /** Set an administrator's photograph. JPG or PNG, 5MB or smaller. */
  uploadAdministratorPhoto(
    schoolId: number,
    userId: number,
    file: File
  ): Observable<Administrator> {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<Administrator>(
      `${this.base}/schools/${schoolId}/administrators/${userId}/photo`,
      form
    );
  }

  /** Correct an administrator's details, or change what they administer. */
  updateAdministrator(
    schoolId: number,
    userId: number,
    payload: AdministratorUpdate
  ): Observable<Administrator> {
    return this.http.patch<Administrator>(
      `${this.base}/schools/${schoolId}/administrators/${userId}`,
      payload
    );
  }

  // --------------------------------------------------------- Academic years
  listAcademicYears(): Observable<AcademicYear[]> {
    return this.http.get<AcademicYear[]>(`${this.base}/academic-years`);
  }

  createAcademicYear(payload: AcademicYearInput): Observable<AcademicYear> {
    return this.http.post<AcademicYear>(`${this.base}/academic-years`, payload);
  }

  updateAcademicYear(id: number, payload: AcademicYearUpdate): Observable<AcademicYear> {
    return this.http.patch<AcademicYear>(`${this.base}/academic-years/${id}`, payload);
  }

  setCurrentAcademicYear(id: number): Observable<AcademicYear> {
    return this.http.post<AcademicYear>(`${this.base}/academic-years/${id}/set-current`, {});
  }

  // ---------------------------------------------------- Classes and sections
  /** `academicYearId` reads another year in the same branch — promotion needs next year's sections. */
  listClasses(academicYearId?: number): Observable<SchoolClass[]> {
    return this.http.get<SchoolClass[]>(`${this.base}/classes`, {
      params: toParams({ academic_year_id: academicYearId })
    });
  }

  createClass(payload: ClassCreate): Observable<SchoolClass> {
    return this.http.post<SchoolClass>(`${this.base}/classes`, payload);
  }

  updateClass(id: number, payload: ClassUpdate): Observable<SchoolClass> {
    return this.http.patch<SchoolClass>(`${this.base}/classes/${id}`, payload);
  }

  createSection(classId: number, payload: SectionInput): Observable<Section> {
    return this.http.post<Section>(`${this.base}/classes/${classId}/sections`, payload);
  }

  updateSection(classId: number, sectionId: number, payload: SectionUpdate): Observable<Section> {
    return this.http.patch<Section>(
      `${this.base}/classes/${classId}/sections/${sectionId}`,
      payload
    );
  }

  /** 409s when anything is enrolled, timetabled or examined under the class. */
  deleteClass(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/classes/${id}`);
  }

  /** 409s when anything is enrolled, timetabled or examined in the section. */
  deleteSection(classId: number, sectionId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/classes/${classId}/sections/${sectionId}`);
  }

  // --------------------------------------------------------------- Subjects
  listSubjects(q?: string): Observable<Subject[]> {
    return this.http.get<Subject[]>(`${this.base}/subjects`, { params: toParams({ q }) });
  }

  createSubject(payload: SubjectInput): Observable<Subject> {
    return this.http.post<Subject>(`${this.base}/subjects`, payload);
  }

  updateSubject(id: number, payload: SubjectUpdate): Observable<Subject> {
    return this.http.patch<Subject>(`${this.base}/subjects/${id}`, payload);
  }

  /** Only for a subject nothing points at; the API refuses the rest with a 409. */
  deleteSubject(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/subjects/${id}`);
  }

  // --------------------------------------------------------------- Teachers
  listTeachers(query: { q?: string; page?: number; page_size?: number } = {}): Observable<Page<Teacher>> {
    return this.http.get<Page<Teacher>>(`${this.base}/teachers`, { params: toParams(query) });
  }

  getTeacher(id: number): Observable<Teacher> {
    return this.http.get<Teacher>(`${this.base}/teachers/${id}`);
  }

  createTeacher(payload: TeacherInput): Observable<Teacher> {
    return this.http.post<Teacher>(`${this.base}/teachers`, payload);
  }

  updateTeacher(id: number, payload: TeacherUpdate): Observable<Teacher> {
    return this.http.patch<Teacher>(`${this.base}/teachers/${id}`, payload);
  }

  // --------------------------------------------------------------- Students
  listStudents(
    query: {
      q?: string;
      class_id?: number;
      section_id?: number;
      status?: string;
      /** name | admission_no | class | roll | status */
      sort?: string;
      direction?: 'asc' | 'desc';
      page?: number;
      page_size?: number;
    } = {}
  ): Observable<Page<Student>> {
    return this.http.get<Page<Student>>(`${this.base}/students`, { params: toParams(query) });
  }

  /** Who the caller is academically: their own record, or their children. */
  myStudents(): Observable<Student[]> {
    return this.http.get<Student[]>(`${this.base}/me/students`);
  }

  /**
   * One section's class list, with attendance and guardian.
   *
   * Which sections a teacher may ask for is decided by the server from their
   * assignments — changing the id in the URL reaches nothing new.
   */
  sectionRoster(sectionId: number, q?: string): Observable<SectionRoster> {
    return this.http.get<SectionRoster>(`${this.base}/sections/${sectionId}/students`, {
      params: toParams({ q })
    });
  }

  getStudent(id: number): Observable<StudentDetail> {
    return this.http.get<StudentDetail>(`${this.base}/students/${id}`);
  }

  createStudent(payload: StudentInput): Observable<Student> {
    return this.http.post<Student>(`${this.base}/students`, payload);
  }

  updateStudent(id: number, payload: StudentUpdate): Observable<Student> {
    return this.http.patch<Student>(`${this.base}/students/${id}`, payload);
  }

  // ------------------------------------------------------------ Enrollments
  createEnrollment(studentId: number, payload: EnrollmentInput): Observable<Enrollment> {
    return this.http.post<Enrollment>(`${this.base}/students/${studentId}/enrollments`, payload);
  }

  updateEnrollment(
    studentId: number,
    enrollmentId: number,
    payload: EnrollmentUpdate
  ): Observable<Enrollment> {
    return this.http.patch<Enrollment>(
      `${this.base}/students/${studentId}/enrollments/${enrollmentId}`,
      payload
    );
  }

  // ---------------------------------------------------------------- Parents
  /** `school_id` narrows the Super Admin's platform view; others are branch-scoped server-side. */
  listParents(
    query: { q?: string; school_id?: number; page?: number; page_size?: number } = {}
  ): Observable<Page<Parent>> {
    return this.http.get<Page<Parent>>(`${this.base}/parents`, { params: toParams(query) });
  }

  /** Upload or replace a parent's photograph. Multipart, like the school logo. */
  uploadParentPhoto(id: number, file: File): Observable<Parent> {
    const body = new FormData();
    body.append('file', file);
    return this.http.post<Parent>(`${this.base}/parents/${id}/photo`, body);
  }

  // ------------------------------------------------------- Your own record
  myProfile(): Observable<MyProfile> {
    return this.http.get<MyProfile>(`${this.base}/me/profile`);
  }

  updateMyProfile(payload: MyProfileUpdate): Observable<MyProfile> {
    return this.http.patch<MyProfile>(`${this.base}/me/profile`, payload);
  }

  // ------------------------------------ A guardian confirming their children
  /** Children this login has been listed against but has not answered for. */
  myGuardianRequests(): Observable<GuardianRequest[]> {
    return this.http.get<GuardianRequest[]>(`${this.base}/me/guardian-requests`);
  }

  /** Confirm a child. They appear in this login's portal from here on. */
  acceptGuardianRequest(linkId: number): Observable<GuardianRequest[]> {
    return this.http.post<GuardianRequest[]>(`${this.base}/me/guardian-requests/${linkId}/accept`, {});
  }

  /** Not this login's child. The link is removed. */
  declineGuardianRequest(linkId: number): Observable<GuardianRequest[]> {
    return this.http.post<GuardianRequest[]>(`${this.base}/me/guardian-requests/${linkId}/decline`, {});
  }

  /** Upload or replace a teacher's photograph. */
  uploadTeacherPhoto(id: number, file: File): Observable<Teacher> {
    const body = new FormData();
    body.append('file', file);
    return this.http.post<Teacher>(`${this.base}/teachers/${id}/photo`, body);
  }

  /** Upload or replace a student's photograph. */
  uploadStudentPhoto(id: number, file: File): Observable<Student> {
    const body = new FormData();
    body.append('file', file);
    return this.http.post<Student>(`${this.base}/students/${id}/photo`, body);
  }

  /** Your own photograph — lands on your person record, or your account. */
  uploadMyPhoto(file: File): Observable<MyProfile> {
    const body = new FormData();
    body.append('file', file);
    return this.http.post<MyProfile>(`${this.base}/me/photo`, body);
  }

  /** The mark that signs whatever this account issues — a Principal's, today.
   *  Only an account with no person record behind it may hold one. */
  uploadMySignature(file: File): Observable<MyProfile> {
    const body = new FormData();
    body.append('file', file);
    return this.http.post<MyProfile>(`${this.base}/me/signature`, body);
  }

  createParent(payload: ParentInput): Observable<Parent> {
    return this.http.post<Parent>(`${this.base}/parents`, payload);
  }

  updateParent(id: number, payload: ParentUpdate): Observable<Parent> {
    return this.http.patch<Parent>(`${this.base}/parents/${id}`, payload);
  }

  // --------------------------------------------------- Teaching assignments
  listTeachingAssignments(
    query: { section_id?: number; teacher_id?: number; subject_id?: number; mine?: boolean } = {}
  ): Observable<TeacherAssignment[]> {
    return this.http.get<TeacherAssignment[]>(`${this.base}/teaching-assignments`, {
      params: toParams(query)
    });
  }

  /** Which subjects in each section still have nobody teaching them. */
  assignmentCoverage(): Observable<CoverageRow[]> {
    return this.http.get<CoverageRow[]>(`${this.base}/teaching-assignments/coverage`);
  }

  createTeachingAssignment(payload: TeacherAssignmentInput): Observable<TeacherAssignment> {
    return this.http.post<TeacherAssignment>(`${this.base}/teaching-assignments`, payload);
  }

  updateTeachingAssignment(
    id: number,
    payload: TeacherAssignmentUpdate
  ): Observable<TeacherAssignment> {
    return this.http.patch<TeacherAssignment>(`${this.base}/teaching-assignments/${id}`, payload);
  }

  // ------------------------------------------------- The shape of a class day
  /** Periods, breaks and days. The grid is drawn from this, not from a constant. */
  getClassFormat(classId: number): Observable<ClassFormat> {
    return this.http.get<ClassFormat>(`${this.base}/classes/${classId}/format`);
  }

  /** PUT, not PATCH: the rows are only meaningful together. */
  setClassFormat(classId: number, payload: ClassFormatInput): Observable<ClassFormat> {
    return this.http.put<ClassFormat>(`${this.base}/classes/${classId}/format`, payload);
  }

  // ---------------------------------------- Subjects a section actually offers
  listSectionSubjects(sectionId: number): Observable<ClassSubject[]> {
    return this.http.get<ClassSubject[]>(`${this.base}/sections/${sectionId}/subjects`);
  }

  /** Pass `subject_id` for one already in the catalogue, or `name` to create it. */
  addSectionSubject(sectionId: number, payload: ClassSubjectAdd): Observable<ClassSubject> {
    return this.http.post<ClassSubject>(`${this.base}/sections/${sectionId}/subjects`, payload);
  }

  dropSectionSubject(sectionId: number, subjectId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/sections/${sectionId}/subjects/${subjectId}`);
  }

  listClassTeachers(): Observable<ClassTeacherLink[]> {
    return this.http.get<ClassTeacherLink[]>(`${this.base}/class-teachers`);
  }

  /** PUT, not POST: a section has one class teacher, so this sets a value. */
  setClassTeacher(sectionId: number, teacherId: number): Observable<ClassTeacherLink> {
    return this.http.put<ClassTeacherLink>(`${this.base}/sections/${sectionId}/class-teacher`, {
      teacher_id: teacherId,
      is_primary: true
    });
  }

  // --------------------------------------------------------------- Timetable
  listTimetable(
    query: { section_id?: number; teacher_id?: number; mine?: boolean } = {}
  ): Observable<TimetableSlot[]> {
    return this.http.get<TimetableSlot[]>(`${this.base}/timetable`, { params: toParams(query) });
  }

  createTimetableSlot(payload: TimetableSlotInput): Observable<TimetableSlot> {
    return this.http.post<TimetableSlot>(`${this.base}/timetable`, payload);
  }

  updateTimetableSlot(id: number, payload: TimetableSlotUpdate): Observable<TimetableSlot> {
    return this.http.patch<TimetableSlot>(`${this.base}/timetable/${id}`, payload);
  }

  deleteTimetableSlot(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/timetable/${id}`);
  }

  publishTimetable(sectionId: number): Observable<TimetableSlot[]> {
    return this.http.post<TimetableSlot[]>(`${this.base}/timetable/publish`, null, {
      params: toParams({ section_id: sectionId })
    });
  }

  // -------------------------------------------------------------- Attendance
  /** Every section's standing for one day, including those with no register. */
  attendanceDay(sessionDate: string): Observable<AttendanceDay[]> {
    return this.http.get<AttendanceDay[]>(`${this.base}/attendance/day`, {
      params: toParams({ session_date: sessionDate })
    });
  }

  /** Every section against every day in a short window — the week view. */
  attendanceGrid(dateFrom: string, dateTo: string): Observable<AttendanceGrid> {
    return this.http.get<AttendanceGrid>(`${this.base}/attendance/grid`, {
      params: toParams({ date_from: dateFrom, date_to: dateTo })
    });
  }

  /** Attendance rolled up per day or per month across every section. */
  attendanceTrend(
    dateFrom: string,
    dateTo: string,
    bucket: 'day' | 'month'
  ): Observable<AttendanceTrend> {
    return this.http.get<AttendanceTrend>(`${this.base}/attendance/trend`, {
      params: toParams({ date_from: dateFrom, date_to: dateTo, bucket })
    });
  }

  listAttendanceSessions(
    query: { section_id?: number; date_from?: string; date_to?: string } = {}
  ): Observable<AttendanceSession[]> {
    return this.http.get<AttendanceSession[]>(`${this.base}/attendance/sessions`, {
      params: toParams(query)
    });
  }

  /** The register for a section on a day — the roster comes back either way. */
  getRegister(sectionId: number, sessionDate: string): Observable<AttendanceRegister> {
    return this.http.get<AttendanceRegister>(`${this.base}/attendance/register`, {
      params: toParams({ section_id: sectionId, session_date: sessionDate })
    });
  }

  submitRegister(payload: AttendanceSubmission): Observable<AttendanceRegister> {
    return this.http.post<AttendanceRegister>(`${this.base}/attendance/register`, payload);
  }

  lockAttendance(sessionId: number): Observable<AttendanceSession> {
    return this.http.post<AttendanceSession>(
      `${this.base}/attendance/sessions/${sessionId}/lock`,
      null
    );
  }

  reopenAttendance(sessionId: number, reason: string): Observable<AttendanceSession> {
    return this.http.post<AttendanceSession>(
      `${this.base}/attendance/sessions/${sessionId}/reopen`,
      null,
      { params: toParams({ reason }) }
    );
  }

  // ------------------------------------------------------------------ Exams
  listExams(): Observable<Exam[]> {
    return this.http.get<Exam[]>(`${this.base}/exams`);
  }

  createExam(payload: ExamInput): Observable<Exam> {
    return this.http.post<Exam>(`${this.base}/exams`, payload);
  }

  /** Also how the lifecycle moves — one step at a time, server-validated. */
  updateExam(id: number, payload: ExamUpdate): Observable<Exam> {
    return this.http.patch<Exam>(`${this.base}/exams/${id}`, payload);
  }

  /** 409s once any mark has been entered against the exam's papers. */
  deleteExam(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/exams/${id}`);
  }

  listPapers(examId: number, query: { section_id?: number; mine?: boolean } = {}): Observable<ExamPaper[]> {
    return this.http.get<ExamPaper[]>(`${this.base}/exams/${examId}/papers`, {
      params: toParams(query)
    });
  }

  createPaper(examId: number, payload: ExamPaperInput): Observable<ExamPaper> {
    return this.http.post<ExamPaper>(`${this.base}/exams/${examId}/papers`, payload);
  }

  updatePaper(paperId: number, payload: ExamPaperUpdate): Observable<ExamPaper> {
    return this.http.patch<ExamPaper>(`${this.base}/papers/${paperId}`, payload);
  }

  /** Every paper with its entered/expected counts — one request, not one each. */
  marksProgress(examId: number): Observable<ExamPaper[]> {
    return this.http.get<ExamPaper[]>(`${this.base}/exams/${examId}/progress`);
  }

  // ------------------------------------------------------------------ Marks
  getMarksSheet(paperId: number): Observable<MarksSheet> {
    return this.http.get<MarksSheet>(`${this.base}/papers/${paperId}/marks`);
  }

  submitMarks(paperId: number, payload: MarksSubmission): Observable<MarksSheet> {
    return this.http.post<MarksSheet>(`${this.base}/papers/${paperId}/marks`, payload);
  }

  // ---------------------------------------------------------------- Results
  listResults(examId: number): Observable<ExamResult[]> {
    return this.http.get<ExamResult[]>(`${this.base}/exams/${examId}/results`);
  }

  calculateResults(examId: number): Observable<ExamResult[]> {
    return this.http.post<ExamResult[]>(`${this.base}/exams/${examId}/results/calculate`, null);
  }

  publishResults(examId: number): Observable<ExamResult[]> {
    return this.http.post<ExamResult[]>(`${this.base}/exams/${examId}/results/publish`, null);
  }

  /** One student's whole history. A family may read only their own. */
  /** A child's attendance and the days that need explaining. */
  studentAttendance(studentId: number): Observable<StudentAttendance> {
    return this.http.get<StudentAttendance>(`${this.base}/students/${studentId}/attendance`);
  }

  academicRecord(studentId: number): Observable<AcademicRecord> {
    return this.http.get<AcademicRecord>(`${this.base}/students/${studentId}/record`);
  }

  // -------------------------------------------------------------- Promotion
  promotionCandidates(sectionId?: number): Observable<PromotionCandidate[]> {
    return this.http.get<PromotionCandidate[]>(`${this.base}/promotion/candidates`, {
      params: toParams({ section_id: sectionId })
    });
  }

  runPromotion(payload: {
    target_academic_year_id: number;
    start_date: string;
    decisions: PromotionDecision[];
  }): Observable<PromotionOutcome> {
    return this.http.post<PromotionOutcome>(`${this.base}/promotion/run`, payload);
  }

  // --------------------------------------------------------------- Homework
  listHomework(query: { section_id?: number; state?: string } = {}): Observable<Homework[]> {
    return this.http.get<Homework[]>(`${this.base}/assignments`, { params: toParams(query) });
  }

  createHomework(payload: HomeworkInput): Observable<Homework> {
    return this.http.post<Homework>(`${this.base}/assignments`, payload);
  }

  updateHomework(id: number, payload: HomeworkUpdate): Observable<Homework> {
    return this.http.patch<Homework>(`${this.base}/assignments/${id}`, payload);
  }

  /** Works only while nothing has been handed in — the server refuses (409) otherwise. */
  deleteHomework(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/assignments/${id}`);
  }

  // ------------------------------------------------------------ Submissions
  /** The whole roster for one piece of homework, submissions joined on. */
  listSubmissions(assignmentId: number): Observable<SubmissionBoard> {
    return this.http.get<SubmissionBoard>(`${this.base}/assignments/${assignmentId}/submissions`);
  }

  /** Hand in your own work. The student comes from the login, not the call. */
  submitHomework(assignmentId: number, note: string | null): Observable<Submission> {
    return this.http.post<Submission>(
      `${this.base}/assignments/${assignmentId}/submissions`,
      { note }
    );
  }

  /**
   * Record a hand-in for a student who did the work off the platform. Only the
   * teacher who set the homework, only onto their roster.
   */
  recordHandIn(
    assignmentId: number,
    studentId: number,
    note: string | null
  ): Observable<Submission> {
    return this.http.post<Submission>(
      `${this.base}/assignments/${assignmentId}/submissions`,
      { student_id: studentId, note }
    );
  }

  withdrawHomework(assignmentId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/assignments/${assignmentId}/submissions`);
  }

  gradeSubmission(
    assignmentId: number,
    studentId: number,
    payload: { score: number; feedback?: string | null }
  ): Observable<Submission> {
    return this.http.patch<Submission>(
      `${this.base}/assignments/${assignmentId}/submissions/${studentId}`,
      payload
    );
  }

  // --------------------------------------------------- Admitting in bulk
  /**
   * Read an intake file and report what is in it. Writes nothing.
   *
   * Sent as multipart with no Content-Type of our own: the browser has to set
   * the boundary, and naming the type here would break the upload.
   */
  previewStudentImport(file: File): Observable<ImportPreview> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<ImportPreview>(`${this.base}/student-import/preview`, form);
  }

  /** Admit the rows the office accepted. Re-checked server-side. */
  commitStudentImport(rows: Record<string, string>[]): Observable<ImportResult> {
    return this.http.post<ImportResult>(`${this.base}/student-import`, { rows });
  }

  /** The sample workbook, fetched as a blob so the download carries the auth header. */
  studentImportSample(): Observable<Blob> {
    return this.http.get(`${this.base}/student-import/sample`, { responseType: 'blob' });
  }


  previewTeacherImport(file: File): Observable<ImportPreview> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<ImportPreview>(`${this.base}/teacher-import/preview`, form);
  }

  /** Add the rows the office accepted. Re-checked server-side. */
  commitTeacherImport(rows: Record<string, string>[]): Observable<TeacherImportResult> {
    return this.http.post<TeacherImportResult>(`${this.base}/teacher-import`, { rows });
  }

  teacherImportSample(): Observable<Blob> {
    return this.http.get(`${this.base}/teacher-import/sample`, { responseType: 'blob' });
  }

  // ------------------------------------------------ Importing parents
  //
  // Platform-level, unlike the others: these rows belong to no school until
  // one links them to a child, so there is no school or branch to send.

  previewParentImport(file: File): Observable<ImportPreview> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<ImportPreview>(`${this.base}/parent-import/preview`, form);
  }

  /** Put the accepted rows on record. Re-checked server-side. */
  commitParentImport(rows: Record<string, string>[]): Observable<ParentImportResult> {
    return this.http.post<ParentImportResult>(`${this.base}/parent-import`, { rows });
  }

  parentImportSample(): Observable<Blob> {
    return this.http.get(`${this.base}/parent-import/sample`, { responseType: 'blob' });
  }

  // ------------------------------------------- Importing class structure
  /** Read a class/section file and report what is in it. Writes nothing. */
  previewClassImport(file: File): Observable<StructureImportPreview> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<StructureImportPreview>(`${this.base}/class-import/preview`, form);
  }

  /** Create the classes and sections the office accepted. Re-checked server-side. */
  commitClassImport(rows: Record<string, string>[]): Observable<StructureImportResult> {
    return this.http.post<StructureImportResult>(`${this.base}/class-import`, { rows });
  }

  classImportSample(): Observable<Blob> {
    return this.http.get(`${this.base}/class-import/sample`, { responseType: 'blob' });
  }

  // ------------------------------------------------------- Removing people
  /**
   * Delete a person record that has nothing attached to it.
   *
   * The server refuses with a 409 the moment there is history — a delete here
   * is a correction for a row typed by mistake, not a way to close a record.
   */
  deleteParent(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/parents/${id}`);
  }

  deleteTeacher(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/teachers/${id}`);
  }

  deleteStudent(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/students/${id}`);
  }

  // ----------------------------------------------------------- Family links
  /**
   * Attach a child to a parent, or a guardian to a child.
   *
   * The same row either way — which end you call from is only about which
   * record the person happens to be looking at.
   */
  linkChild(parentId: number, payload: ChildLinkInput): Observable<Parent> {
    return this.http.post<Parent>(`${this.base}/parents/${parentId}/children`, payload);
  }

  linkGuardian(studentId: number, payload: GuardianLinkInput): Observable<GuardianLink[]> {
    return this.http.post<GuardianLink[]>(
      `${this.base}/students/${studentId}/guardians`,
      payload
    );
  }

  updateFamilyLink(linkId: number, payload: ChildLinkUpdate): Observable<GuardianLink[]> {
    return this.http.patch<GuardianLink[]>(`${this.base}/parent-links/${linkId}`, payload);
  }

  removeFamilyLink(linkId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/parent-links/${linkId}`);
  }

  /** One child's homework and where they have got to with each piece. */
  studentHomework(studentId: number): Observable<StudentHomework[]> {
    return this.http.get<StudentHomework[]>(`${this.base}/assignments/students/${studentId}`);
  }

  // ------------------------------------------------------------ Staff tasks
  /** Teachers and office staff in this branch a task can be given to. */
  listTaskAssignees(): Observable<TaskAssignee[]> {
    return this.http.get<TaskAssignee[]>(`${this.base}/staff-tasks/assignees`);
  }

  listStaffTasks(query: { status?: string; teacher_id?: number } = {}): Observable<StaffTask[]> {
    return this.http.get<StaffTask[]>(`${this.base}/staff-tasks`, { params: toParams(query) });
  }

  staffTaskCounts(): Observable<StaffTaskCounts> {
    return this.http.get<StaffTaskCounts>(`${this.base}/staff-tasks/summary`);
  }

  createStaffTask(payload: StaffTaskInput): Observable<StaffTask> {
    return this.http.post<StaffTask>(`${this.base}/staff-tasks`, payload);
  }

  updateStaffTask(id: number, payload: StaffTaskUpdate): Observable<StaffTask> {
    return this.http.patch<StaffTask>(`${this.base}/staff-tasks/${id}`, payload);
  }

  deleteStaffTask(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/staff-tasks/${id}`);
  }

  // ------------------------------------------------------ SMS & WhatsApp
  messagingSettings(): Observable<MessagingSettings> {
    return this.http.get<MessagingSettings>(`${this.base}/messaging/settings`);
  }

  updateMessagingSettings(payload: MessagingSettingsUpdate): Observable<MessagingSettings> {
    return this.http.put<MessagingSettings>(`${this.base}/messaging/settings`, payload);
  }

  listMessages(query: { status?: string; event?: string; q?: string; page?: number; page_size?: number } = {}): Observable<Page<OutboundMessage>> {
    return this.http.get<Page<OutboundMessage>>(`${this.base}/messaging/outbox`, { params: toParams(query) });
  }

  outboxSummary(days = 30): Observable<OutboxSummary> {
    return this.http.get<OutboxSummary>(`${this.base}/messaging/outbox/summary`, { params: toParams({ days }) });
  }

  retryMessage(id: number): Observable<OutboundMessage> {
    return this.http.post<OutboundMessage>(`${this.base}/messaging/outbox/${id}/retry`, {});
  }

  sendTestMessage(payload: { phone: string; channel: 'SMS' | 'WHATSAPP'; language: 'en' | 'kn' }): Observable<OutboundMessage> {
    return this.http.post<OutboundMessage>(`${this.base}/messaging/test`, payload);
  }

  sendFeeReminders(payload: { kind: 'DUE_SOON' | 'OVERDUE'; within_days?: number; class_id?: number | null }): Observable<FeeReminderResult> {
    return this.http.post<FeeReminderResult>(`${this.base}/messaging/fee-reminders`, payload);
  }

  myMessagingPreferences(): Observable<MessagingPreferences> {
    return this.http.get<MessagingPreferences>(`${this.base}/me/messaging-preferences`);
  }

  updateMyMessagingPreferences(payload: Partial<MessagingPreferences>): Observable<MessagingPreferences> {
    return this.http.patch<MessagingPreferences>(`${this.base}/me/messaging-preferences`, payload);
  }

  // ------------------------------------------------------------- Calendar
  listCalendarEvents(query: { from?: string; to?: string; kind?: string } = {}): Observable<CalendarEvent[]> {
    return this.http.get<CalendarEvent[]>(`${this.base}/calendar/events`, { params: toParams(query) });
  }

  createCalendarEvent(payload: CalendarEventInput): Observable<CalendarEvent> {
    return this.http.post<CalendarEvent>(`${this.base}/calendar/events`, payload);
  }

  updateCalendarEvent(id: number, payload: Partial<CalendarEventInput>): Observable<CalendarEvent> {
    return this.http.patch<CalendarEvent>(`${this.base}/calendar/events/${id}`, payload);
  }

  deleteCalendarEvent(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/calendar/events/${id}`);
  }

  // ---------------------------------------------------------------- Leave
  leaveOptions(): Observable<LeaveOptions> {
    return this.http.get<LeaveOptions>(`${this.base}/leave/options`);
  }

  listLeave(scope: 'mine' | 'approvals', status?: string): Observable<LeaveRequest[]> {
    return this.http.get<LeaveRequest[]>(`${this.base}/leave`, { params: toParams({ scope, status }) });
  }

  requestLeave(payload: LeaveRequestInput): Observable<LeaveRequest> {
    return this.http.post<LeaveRequest>(`${this.base}/leave`, payload);
  }

  decideLeave(id: number, decision: 'APPROVED' | 'REJECTED', note: string | null): Observable<LeaveRequest> {
    return this.http.post<LeaveRequest>(`${this.base}/leave/${id}/decide`, { decision, note });
  }

  cancelLeave(id: number): Observable<LeaveRequest> {
    return this.http.post<LeaveRequest>(`${this.base}/leave/${id}/cancel`, {});
  }

  // ------------------------------------------------------------ Admissions
  listAdmissions(query: { stage?: string; class_id?: number; q?: string; follow_up_due?: boolean; page?: number; page_size?: number } = {}): Observable<Page<AdmissionApplication>> {
    return this.http.get<Page<AdmissionApplication>>(`${this.base}/admissions`, { params: toParams(query) });
  }

  admissionsSummary(): Observable<AdmissionSummary> {
    return this.http.get<AdmissionSummary>(`${this.base}/admissions/summary`);
  }

  getAdmission(id: number): Observable<AdmissionDetail> {
    return this.http.get<AdmissionDetail>(`${this.base}/admissions/${id}`);
  }

  createAdmission(payload: AdmissionInput): Observable<AdmissionApplication> {
    return this.http.post<AdmissionApplication>(`${this.base}/admissions`, payload);
  }

  updateAdmission(id: number, payload: Partial<AdmissionInput>): Observable<AdmissionApplication> {
    return this.http.patch<AdmissionApplication>(`${this.base}/admissions/${id}`, payload);
  }

  moveAdmissionStage(id: number, payload: { stage: string; note: string | null; notify: boolean }): Observable<AdmissionApplication> {
    return this.http.post<AdmissionApplication>(`${this.base}/admissions/${id}/stage`, payload);
  }

  nextAdmissionNo(): Observable<{ admission_no: string }> {
    return this.http.get<{ admission_no: string }>(`${this.base}/admissions/next-admission-no`);
  }

  convertAdmission(id: number, payload: AdmissionConvertInput): Observable<AdmissionConvertResult> {
    return this.http.post<AdmissionConvertResult>(`${this.base}/admissions/${id}/convert`, payload);
  }

  // ---------------------------------------------------------- Certificates
  listCertificates(query: { student_id?: number; kind?: string; q?: string; page?: number; page_size?: number } = {}): Observable<Page<Certificate>> {
    return this.http.get<Page<Certificate>>(`${this.base}/certificates`, { params: toParams(query) });
  }

  issueCertificate(payload: CertificateInput): Observable<Certificate> {
    return this.http.post<Certificate>(`${this.base}/certificates`, payload);
  }

  certificatePdf(id: number): Observable<Blob> {
    return this.http.get(`${this.base}/certificates/${id}/pdf`, { responseType: 'blob' });
  }

  revokeCertificate(id: number, reason: string): Observable<Certificate> {
    return this.http.post<Certificate>(`${this.base}/certificates/${id}/revoke`, { reason });
  }

  /** Unauthenticated: anyone holding the paper may check it. */
  verifyCertificate(token: string): Observable<CertificateVerification> {
    return this.http.get<CertificateVerification>(`${this.base}/public/certificates/${encodeURIComponent(token)}`);
  }

  // ------------------------------------------------------------- Governance
  listAuditLogs(
    query: {
      action?: string;
      actor?: string;
      target_type?: string;
      result?: string;
      q?: string;
      school_id?: number;
      date_from?: string;
      date_to?: string;
      page?: number;
      page_size?: number;
    } = {}
  ): Observable<Page<AuditEntry>> {
    return this.http.get<Page<AuditEntry>>(`${this.base}/audit-logs`, { params: toParams(query) });
  }

  /** The dropdown values, built from what is actually in the caller's trail. */
  auditFilters(): Observable<AuditFilters> {
    return this.http.get<AuditFilters>(`${this.base}/audit-logs/filters`);
  }

  // ---------------------------------------------------------------- Notices
  /** What the caller may read — the audience is applied server-side. */
  listAnnouncements(query: { mine?: boolean } = {}): Observable<Announcement[]> {
    return this.http.get<Announcement[]>(`${this.base}/announcements`, { params: toParams(query) });
  }

  /** Who can be written to, and how many people each choice reaches. */
  audienceOptions(): Observable<AudienceOption[]> {
    return this.http.get<AudienceOption[]>(`${this.base}/announcements/audiences`);
  }

  createAnnouncement(payload: AnnouncementInput): Observable<Announcement> {
    return this.http.post<Announcement>(`${this.base}/announcements`, payload);
  }

  updateAnnouncement(id: number, payload: AnnouncementUpdate): Observable<Announcement> {
    return this.http.patch<Announcement>(`${this.base}/announcements/${id}`, payload);
  }

  /** Sending it. Without a time it goes now; a future time schedules it. */
  publishAnnouncement(id: number, publishAt?: string): Observable<Announcement> {
    return this.http.post<Announcement>(`${this.base}/announcements/${id}/publish`, null, {
      params: toParams({ publish_at: publishAt })
    });
  }

  /** The signed-in student's own exam timetable. No id: it reads their login. */
  myExams(): Observable<StudentExam[]> {
    return this.http.get<StudentExam[]>(`${this.base}/me/exams`);
  }

  listNotifications(query: { unread_only?: boolean; limit?: number } = {}): Observable<AppNotification[]> {
    return this.http.get<AppNotification[]>(`${this.base}/notifications`, { params: toParams(query) });
  }

  unreadCount(): Observable<{ unread: number }> {
    return this.http.get<{ unread: number }>(`${this.base}/notifications/unread-count`);
  }

  markNotificationRead(id: number): Observable<AppNotification> {
    return this.http.post<AppNotification>(`${this.base}/notifications/${id}/read`, null);
  }

  markAllNotificationsRead(): Observable<{ unread: number }> {
    return this.http.post<{ unread: number }>(`${this.base}/notifications/read-all`, null);
  }

  // ------------------------------------------------------------- Dashboards
  superAdminDashboard(): Observable<SuperAdminDashboard> {
    return this.http.get<SuperAdminDashboard>(`${this.base}/dashboard/super-admin`);
  }

  schoolAdminDashboard(sessionDate?: string): Observable<SchoolAdminDashboard> {
    return this.http.get<SchoolAdminDashboard>(`${this.base}/dashboard/school-admin`, {
      params: toParams({ session_date: sessionDate })
    });
  }

  teacherDashboard(sessionDate?: string): Observable<TeacherDashboard> {
    return this.http.get<TeacherDashboard>(`${this.base}/dashboard/teacher`, {
      params: toParams({ session_date: sessionDate })
    });
  }

  parentDashboard(): Observable<ParentDashboard> {
    return this.http.get<ParentDashboard>(`${this.base}/dashboard/parent`);
  }

  studentDashboard(): Observable<StudentDashboard> {
    return this.http.get<StudentDashboard>(`${this.base}/dashboard/student`);
  }

  // ------------------------------------------------------------------- Fees
  listFeeStructures(): Observable<FeeStructure[]> {
    return this.http.get<FeeStructure[]>(`${this.base}/fee-structures`);
  }

  createFeeStructure(payload: FeeStructureInput): Observable<FeeStructure> {
    return this.http.post<FeeStructure>(`${this.base}/fee-structures`, payload);
  }

  updateFeeStructure(id: number, payload: FeeStructureUpdate): Observable<FeeStructure> {
    return this.http.patch<FeeStructure>(`${this.base}/fee-structures/${id}`, payload);
  }

  listInvoices(
    query: { q?: string; status?: string; class_id?: number; student_id?: number } = {}
  ): Observable<FeeInvoice[]> {
    return this.http.get<FeeInvoice[]>(`${this.base}/fee-invoices`, { params: toParams(query) });
  }

  /** What the branch is owed, counted server-side so two screens cannot disagree. */
  feeSummary(): Observable<FeeSummary> {
    return this.http.get<FeeSummary>(`${this.base}/fee-invoices/summary`);
  }

  /** Raising one installment for a set of classes. Re-running skips, never duplicates. */
  generateInvoices(payload: InvoiceRun): Observable<InvoiceRunResult> {
    return this.http.post<InvoiceRunResult>(`${this.base}/fee-invoices/generate`, payload);
  }

  cancelInvoice(id: number, reason: string): Observable<FeeInvoice> {
    return this.http.post<FeeInvoice>(`${this.base}/fee-invoices/${id}/cancel`, null, {
      params: toParams({ reason })
    });
  }

  /**
   * The invoice as a PDF.
   *
   * Fetched rather than linked. A plain href cannot carry the bearer token —
   * only HttpClient goes through the interceptor that adds it — so a link
   * would arrive unauthenticated and hand the person a 401 page where they
   * expected their bill.
   *
   * `design` picks the layout — 'classic' or 'compact' — and defaults to
   * whatever the server defaults to when left out.
   */
  invoicePdf(invoiceId: number, design?: InvoiceDesign): Observable<Blob> {
    return this.http.get(`${this.base}/fee-invoices/${invoiceId}/pdf`, {
      params: toParams({ design }),
      responseType: 'blob'
    });
  }

  /**
   * A child's report card for one exam, as a PDF.
   *
   * Fetched rather than linked, like the invoice, so the request carries the
   * token — a plain link would arrive unauthenticated and hand somebody a 401
   * where they expected their child's marks.
   *
   * `design` picks the layout — 'classic' or 'modern'.
   */
  reportCard(examId: number, studentId: number, design?: ReportCardDesign): Observable<Blob> {
    return this.http.get(`${this.base}/exams/${examId}/students/${studentId}/report-card`, {
      params: toParams({ design }),
      responseType: 'blob'
    });
  }

  /** Every card in a section, one file, one child per page. */
  sectionReportCards(
    examId: number, sectionId?: number | null, design?: ReportCardDesign
  ): Observable<Blob> {
    return this.http.get(`${this.base}/exams/${examId}/report-cards`, {
      params: toParams({ section_id: sectionId ?? undefined, design }),
      responseType: 'blob'
    });
  }

  /** Returns the whole invoice back, so the balance never has to be computed here. */
  recordPayment(invoiceId: number, payload: PaymentInput): Observable<FeeInvoice> {
    return this.http.post<FeeInvoice>(`${this.base}/fee-invoices/${invoiceId}/payments`, payload);
  }

  reversePayment(paymentId: number, reason: string): Observable<FeeInvoice> {
    return this.http.post<FeeInvoice>(`${this.base}/fee-payments/${paymentId}/reverse`, null, {
      params: toParams({ reason })
    });
  }

  listFeePayments(query: { student_id?: number; limit?: number } = {}): Observable<FeePayment[]> {
    return this.http.get<FeePayment[]>(`${this.base}/fee-payments`, { params: toParams(query) });
  }

  /** The caller's own fees, or their children's. Read-only by design. */
  myFees(): Observable<ChildFees[]> {
    return this.http.get<ChildFees[]>(`${this.base}/me/fees`);
  }

  // ------------------------------------------------------------ Provisioning
  listUserAccounts(query: { q?: string; role?: string } = {}): Observable<UserAccount[]> {
    return this.http.get<UserAccount[]>(`${this.base}/users`, { params: toParams(query) });
  }

  /** Issue a login. The temporary password comes back once, in the response. */
  provisionUser(payload: ProvisionInput): Observable<ProvisionResult> {
    return this.http.post<ProvisionResult>(`${this.base}/users`, payload);
  }

  /**
   * Issue a sign-in to one student. Reachable by the office and by the
   * student's own class teacher — the server decides which, from the caller.
   */
  issueStudentLogin(
    studentId: number,
    payload: { email: string; mobile?: string | null; temporary_password: string }
  ): Observable<ProvisionResult> {
    return this.http.post<ProvisionResult>(`${this.base}/students/${studentId}/login`, payload);
  }

  /** Correct a login's name, email or mobile, or move office staff between roles. */
  updateUserAccount(userId: number, payload: UserAccountUpdate): Observable<UserAccount> {
    return this.http.patch<UserAccount>(`${this.base}/users/${userId}`, payload);
  }

  resetUserPassword(userId: number, temporaryPassword: string): Observable<ProvisionResult> {
    return this.http.post<ProvisionResult>(`${this.base}/users/${userId}/reset-password`, {
      temporary_password: temporaryPassword
    });
  }

  setUserStatus(userId: number, isActive: boolean, reason: string): Observable<UserAccount> {
    return this.http.post<UserAccount>(`${this.base}/users/${userId}/status`, null, {
      params: toParams({ is_active: isActive, reason })
    });
  }

  /** Reachable while a first sign-in is pending — it is the only thing that is. */
  changeOwnPassword(currentPassword: string, newPassword: string): Observable<UserAccount> {
    return this.http.post<UserAccount>(`${this.base}/me/password`, {
      current_password: currentPassword,
      new_password: newPassword
    });
  }

  // ---- Exports
  //
  // Spreadsheets of what a screen already shows. Fetched as blobs for the same
  // reason the invoice PDF is: only HttpClient passes through the interceptor
  // that adds the bearer token and the X-School-Id / X-Branch-Id headers, so a
  // plain link to one of these would arrive unauthenticated and scoped to
  // nothing. Note the missing type parameter on `get` — with `<Blob>` TypeScript
  // picks the JSON overload and the response arrives parsed and broken.
  //
  // Every one of these 404s when the server's EXCEL_EXPORT_ENABLED is off,
  // which is how a screen tells that export is switched off rather than broken.

  exportStudents(): Observable<Blob> {
    return this.http.get(`${this.base}/exports/students.xlsx`, { responseType: 'blob' });
  }

  exportParents(): Observable<Blob> {
    return this.http.get(`${this.base}/exports/parents.xlsx`, { responseType: 'blob' });
  }

  exportAttendance(dateFrom: string, dateTo: string, sectionId?: number): Observable<Blob> {
    return this.http.get(`${this.base}/exports/attendance.xlsx`, {
      params: toParams({ date_from: dateFrom, date_to: dateTo, section_id: sectionId }),
      responseType: 'blob'
    });
  }

  exportExams(): Observable<Blob> {
    return this.http.get(`${this.base}/exports/exams.xlsx`, { responseType: 'blob' });
  }

  exportResults(examId: number): Observable<Blob> {
    return this.http.get(`${this.base}/exports/results.xlsx`, {
      params: toParams({ exam_id: examId }),
      responseType: 'blob'
    });
  }

  // ---- Role privileges

  /** This school's matrix, for every role the caller is allowed to tailor. */
  getRolePrivileges(): Observable<PrivilegeMatrix> {
    return this.http.get<PrivilegeMatrix>(`${this.base}/role-privileges`);
  }

  /** Send only what changed; the server keeps anything omitted as it is. */
  updateRolePrivileges(role: string, permissions: Record<string, boolean>): Observable<PrivilegeMatrix> {
    return this.http.put<PrivilegeMatrix>(`${this.base}/role-privileges/${role}`, { permissions });
  }

  resetRolePrivileges(role: string): Observable<PrivilegeMatrix> {
    return this.http.post<PrivilegeMatrix>(`${this.base}/role-privileges/${role}/reset`, {});
  }
}
